import { ArrowLeft, Bell, CreditCard, KeyRound, Layers, Mail, Palette, Users } from 'lucide-react';
import type React from 'react';
import type { ReactNode } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../../ui/button';
import { BuildInfoBadge } from '../build-info-badge';

export interface SettingsNavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to: string;
  description?: string;
  adminOnly?: boolean;
}

export interface SettingsSidebarProps {
  productLabel: string;
  isAdmin?: boolean;
  /** Explicit items to render. When provided, completely defines the nav items. */
  items?: SettingsNavItem[];
  /** App-specific items injected alongside common settings (Accounts, Appearance, Members, etc.) */
  customNavItems?: SettingsNavItem[];
  /** False for products with no mailbox to attach -- the tab is dropped, not emptied. */
  showAccounts?: boolean;
  /** Custom destination for the "Back to App" button (defaults to `${base}/dashboard`). */
  backTo?: string;
  headerSlot?: ReactNode;
  footerSlot?: ReactNode;
}

export function buildDefaultSettingsNavItems(options: {
  base: string;
  isAdmin?: boolean;
  showAccounts?: boolean;
  customNavItems?: SettingsNavItem[];
}): SettingsNavItem[] {
  const { base, isAdmin = false, showAccounts = false, customNavItems = [] } = options;

  const normalizedCustomItems = customNavItems.map((item) => ({
    ...item,
    to: item.to.startsWith('/') ? item.to : `${base}/settings/${item.to}`,
  }));

  const commonItems: SettingsNavItem[] = [
    ...(showAccounts
      ? [
          {
            id: 'accounts',
            label: 'Accounts',
            icon: Mail,
            to: `${base}/settings/accounts`,
            description: 'Connected email & OAuth integrations',
          },
        ]
      : []),
    ...normalizedCustomItems,
    {
      id: 'contact-types',
      label: 'Contact Types',
      icon: Layers,
      to: `${base}/settings/contact-types`,
      description: 'Custom fields & contact schemas',
    },
    {
      id: 'appearance',
      label: 'Appearance',
      icon: Palette,
      to: `${base}/settings/appearance`,
      description: 'Theme preferences & visual mode',
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: Bell,
      to: `${base}/settings/notifications`,
      description: 'Alert preferences & push settings',
    },
    {
      id: 'members',
      label: 'Members',
      icon: Users,
      to: `${base}/settings/members`,
      description: 'Workspace team & seat access',
    },
    ...(isAdmin
      ? [
          {
            id: 'ai-provider',
            label: 'AI Provider',
            icon: KeyRound,
            to: `${base}/settings/ai-provider`,
            description: 'Bring your own AI provider key',
            adminOnly: true,
          },
          {
            id: 'billing',
            label: 'Billing',
            icon: CreditCard,
            to: `${base}/settings/billing`,
            description: 'Plan, token usage & subscription',
            adminOnly: true,
          },
        ]
      : []),
  ];

  return commonItems.filter((item) => !item.adminOnly || isAdmin);
}

export function SettingsSidebar({
  productLabel,
  isAdmin = false,
  items,
  customNavItems = [],
  showAccounts = false,
  backTo,
  headerSlot,
  footerSlot,
}: SettingsSidebarProps) {
  const navigate = useNavigate();
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const base = `/org/${orgId}/workspace/${workspaceId}`;

  const navItems =
    items ??
    buildDefaultSettingsNavItems({
      base,
      isAdmin,
      showAccounts,
      customNavItems,
    });

  const resolvedBackTo = backTo ?? `${base}/dashboard`;

  return (
    <aside className="w-64 shrink-0 border-r border-border bg-sidebar/50 backdrop-blur-md flex flex-col h-full overflow-y-auto">
      {/* Top Header: Back to App */}
      <div className="p-4 border-b border-border/50">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate(resolvedBackTo)}
          className="w-full justify-start text-xs font-semibold text-muted-foreground hover:text-foreground gap-2 px-2.5 py-2 group transition-colors cursor-pointer"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Back to App</span>
        </Button>
      </div>

      {headerSlot}

      {/* Settings Title */}
      <div className="px-5 py-4">
        <h2 className="text-base font-bold tracking-tight text-foreground">Settings</h2>
        <p className="text-xs text-muted-foreground mt-0.5">{productLabel} workspace</p>
      </div>

      {/* Navigation Links */}
      <nav className="px-3 py-2 space-y-1 flex-1">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.id}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-all group',
                  isActive
                    ? 'bg-primary text-primary-foreground font-semibold shadow-[var(--surface-shadow,0_1px_2px_0_rgb(0_0_0_/_0.05))]'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon
                    className={cn(
                      'h-4 w-4 shrink-0 transition-colors',
                      isActive
                        ? 'text-primary-foreground'
                        : 'text-muted-foreground group-hover:text-foreground'
                    )}
                  />
                  <span>{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {footerSlot}

      {/* Footer: Version & Build Stamp */}
      <div className="p-4 border-t border-border/50">
        <BuildInfoBadge appName={`${productLabel} Portal`} />
      </div>
    </aside>
  );
}
